import * as React from 'react';
import { useDynamicModule, unspaceString } from '@grandvisionhq/utils';
import { useIntl } from '@grandvisionhq/state';
import { Select, PhoneNumber } from '@grandvisionhq/forms';
import { Alert } from '@grandvisionhq/common';

function asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator$2(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
const AdyenContext = /*#__PURE__*/ React.createContext({
    clientKey: '',
    environment: 'test',
    AdyenModule: {
        result: undefined,
        loading: true
    }
});
const useAdyen = ()=>React.useContext(AdyenContext)
;
const AdyenProvider = ({ children , settings  })=>{
    // The `@adyen/adyen-web` package uses a hard reference to the global `window` variable. This makes it
    // impossible to import the package in a normal manner. Hence the choice to use dynamic importing.
    const [result, loading] = useDynamicModule(_asyncToGenerator$2(function*() {
        if (typeof window !== 'undefined') {
            return yield import('@adyen/adyen-web').then((module)=>module.default
            );
        }
        return undefined;
    }), '@adyen/adyen-web', []);
    const memoizedSettings = React.useMemo(()=>_objectSpread$1({}, settings, {
            AdyenModule: {
                result: result !== null && result !== void 0 ? result : undefined,
                loading
            }
        })
    , [
        settings,
        loading,
        result
    ]);
    return(/*#__PURE__*/ React.createElement(AdyenContext.Provider, {
        value: memoizedSettings
    }, children));
};

var AdyenPaymentType;
(function(AdyenPaymentType) {
    AdyenPaymentType["Card"] = 'card';
    AdyenPaymentType["Bancontact"] = 'bcmc';
    AdyenPaymentType["Sepa"] = 'sepadirectdebit';
})(AdyenPaymentType || (AdyenPaymentType = {}));

function useCheckout(onChange) {
    const { locale  } = useIntl();
    // Import the adyen web module
    const { AdyenModule , clientKey , environment  } = useAdyen();
    const options = React.useMemo(()=>({
            clientKey,
            environment,
            locale: `${locale}`,
            onChange
        })
    , [
        clientKey,
        environment,
        locale,
        onChange
    ]);
    // The checkout object is local to the current page, it needs to be memoized
    // so to handle the dynamic module loading correctly
    const checkout = React.useMemo(()=>{
        const AdyenCheckout = AdyenModule === null || AdyenModule === void 0 ? void 0 : AdyenModule.result;
        return AdyenCheckout && AdyenCheckout(options);
    }, [
        AdyenModule,
        options
    ]);
    return checkout;
}

function asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator$1(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
const PaymentMethodFormContainer = ({ children , type , additionalOptions , checkout ,  })=>{
    const ref = React.useRef(null);
    // In order to properly unmount any mounted elements, the promise of the mounted element is used to
    // properly unmount it again whenever the cleanup cycle is triggered.
    React.useEffect(()=>{
        if (!checkout) return;
        const mountPayment = function() {
            var _ref = _asyncToGenerator$1(function*() {
                const c = yield checkout;
                const paymentMethod = c === null || c === void 0 ? void 0 : c.create(type, additionalOptions);
                var _current;
                return paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.mount((_current = ref.current) !== null && _current !== void 0 ? _current : '');
            });
            return function mountPayment() {
                return _ref.apply(this, arguments);
            };
        }();
        const promise = mountPayment();
        return ()=>{
            promise.then((component)=>{
                return component === null || component === void 0 ? void 0 : component.unmount();
            });
        };
    }, [
        type,
        additionalOptions,
        checkout
    ]);
    if (!checkout) return null;
    return(/*#__PURE__*/ React.createElement("div", {
        ref: ref,
        className: "adyen-payment-method-form"
    }, children));
};

function _extends$5() {
    _extends$5 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$5.apply(this, arguments);
}
const PaymentMethodForm = (props)=>/*#__PURE__*/ React.createElement(PaymentMethodFormContainer, _extends$5({}, props))
;

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
var ref, ref1, ref2, ref3, ref4;
const toPaymentMethodFormState$1 = (state, cardBrand)=>{
    var ref18, ref5, ref6, ref7, ref8, ref9, ref10, ref11, ref12, ref13, ref14, ref15, ref16, ref17;
    return {
        cardBrand,
        isValid: state.isValid,
        data: {
            browserInfo: {
                acceptHeader: state === null || state === void 0 ? void 0 : (ref18 = state.data) === null || ref18 === void 0 ? void 0 : (ref5 = ref18.browserInfo) === null || ref5 === void 0 ? void 0 : ref5.acceptHeader,
                userAgent: state === null || state === void 0 ? void 0 : (ref6 = state.data) === null || ref6 === void 0 ? void 0 : (ref7 = ref6.browserInfo) === null || ref7 === void 0 ? void 0 : ref7.userAgent
            },
            holderName: (ref = state === null || state === void 0 ? void 0 : (ref8 = state.data) === null || ref8 === void 0 ? void 0 : (ref9 = ref8.paymentMethod) === null || ref9 === void 0 ? void 0 : ref9.holderName) !== null && ref !== void 0 ? ref : '',
            encryptedCardNumber: (ref1 = state === null || state === void 0 ? void 0 : (ref10 = state.data) === null || ref10 === void 0 ? void 0 : (ref11 = ref10.paymentMethod) === null || ref11 === void 0 ? void 0 : ref11.encryptedCardNumber) !== null && ref1 !== void 0 ? ref1 : '',
            encryptedExpiryMonth: (ref2 = state === null || state === void 0 ? void 0 : (ref12 = state.data) === null || ref12 === void 0 ? void 0 : (ref13 = ref12.paymentMethod) === null || ref13 === void 0 ? void 0 : ref13.encryptedExpiryMonth) !== null && ref2 !== void 0 ? ref2 : '',
            encryptedExpiryYear: (ref3 = state === null || state === void 0 ? void 0 : (ref14 = state.data) === null || ref14 === void 0 ? void 0 : (ref15 = ref14.paymentMethod) === null || ref15 === void 0 ? void 0 : ref15.encryptedExpiryYear) !== null && ref3 !== void 0 ? ref3 : '',
            encryptedSecurityCode: (ref4 = state === null || state === void 0 ? void 0 : (ref16 = state.data) === null || ref16 === void 0 ? void 0 : (ref17 = ref16.paymentMethod) === null || ref17 === void 0 ? void 0 : ref17.encryptedSecurityCode) !== null && ref4 !== void 0 ? ref4 : ''
        }
    };
};
const CreditCardContainer = ({ onChange , additionalOptions , type =AdyenPaymentType.Card ,  })=>{
    const [state, setState] = React.useState();
    const [brand, setBrand] = React.useState();
    const checkout = useCheckout(setState);
    const additional = React.useMemo(()=>{
        return _objectSpread({
            hasHolderName: true,
            holderNameRequired: true,
            positionHolderNameOnTop: true
        }, additionalOptions, {
            onBrand: (obj)=>{
                var ref19;
                additionalOptions === null || additionalOptions === void 0 ? void 0 : (ref19 = additionalOptions.onBrand) === null || ref19 === void 0 ? void 0 : ref19.call(additionalOptions, obj);
                setBrand(obj.brand);
            }
        });
    }, [
        additionalOptions,
        setBrand
    ]);
    React.useEffect(()=>{
        if (state) {
            onChange(toPaymentMethodFormState$1(state, brand));
        }
    }, [
        brand,
        state,
        onChange
    ]);
    return(/*#__PURE__*/ React.createElement(PaymentMethodForm, {
        type: type,
        checkout: checkout,
        additionalOptions: additional
    }));
};

function _extends$4() {
    _extends$4 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$4.apply(this, arguments);
}
const CreditCard = (props)=>/*#__PURE__*/ React.createElement(CreditCardContainer, _extends$4({}, props))
;

const IdealContainer = ({ items , onChange  })=>{
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            title: getLabel('checkout.payment.method.ideal.select.title')
        })
    , [
        getLabel
    ]);
    return(/*#__PURE__*/ React.createElement(Select, {
        id: "idealBankSelect",
        name: "idealBankSelect",
        options: items,
        onChange: onChange,
        label: labels.title,
        placeholder: labels.title
    }));
};

function _extends$3() {
    _extends$3 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$3.apply(this, arguments);
}
const Ideal = (props)=>/*#__PURE__*/ React.createElement(IdealContainer, _extends$3({}, props))
;

const Mbway$1 = ({ initialValue , labels , onChange , validations  })=>{
    return(/*#__PURE__*/ React.createElement("div", {
        className: "mbway-payment checkout-payment-wrapper"
    }, /*#__PURE__*/ React.createElement(Alert, {
        className: "mbway-payment__alert",
        title: labels.hintTitle,
        message: labels.hintMessage
    }), /*#__PURE__*/ React.createElement(PhoneNumber, {
        initialState: {
            value: initialValue,
            isTouched: false
        },
        required: true,
        onChange: onChange,
        validations: validations,
        label: labels.phoneNumberLabel,
        placeholder: labels.phoneNumberPlaceholder,
        errorMessage: labels.phoneNumberError
    })));
};

const isValidPortugueseMobilePhoneNumber = (value)=>/^(\+351|00351|351)([9]{1})(6|3|2|1{1})([0-9]{7})$/.test(unspaceString(value))
;

const MbwayContainer = ({ initialValue , onChange  })=>{
    const { getLabel  } = useIntl();
    const handleOnChange = React.useCallback((state)=>{
        onChange({
            telephoneNumber: unspaceString(state.value)
        }, isValidPortugueseMobilePhoneNumber(state.value));
    }, [
        onChange
    ]);
    const labels = React.useMemo(()=>{
        return {
            hintTitle: getLabel('checkout.payment.method.mbWay.hint.title'),
            hintMessage: getLabel('checkout.payment.method.mbWay.hint.message'),
            phoneNumberLabel: getLabel('checkout.contact.phoneNumber'),
            phoneNumberPlaceholder: getLabel('checkout.payment.method.mbWay.phoneNumber.placeholder'),
            phoneNumberError: getLabel('checkout.contact.phoneNumber.errorMessage')
        };
    }, [
        getLabel
    ]);
    return(/*#__PURE__*/ React.createElement(Mbway$1, {
        initialValue: initialValue,
        labels: labels,
        onChange: handleOnChange,
        validations: [
            isValidPortugueseMobilePhoneNumber
        ]
    }));
};

function _extends$2() {
    _extends$2 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$2.apply(this, arguments);
}
const Mbway = (props)=>/*#__PURE__*/ React.createElement(MbwayContainer, _extends$2({}, props))
;

const Oney$1 = ({ options , labels , onChange  })=>/*#__PURE__*/ React.createElement(Select, {
        id: "oneyInstallments",
        name: "oneyInstallments",
        options: options,
        onChange: onChange,
        label: labels.title,
        placeholder: labels.placeholder,
        required: true
    })
;

const OneyContainer = ({ onChange , options  })=>{
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            title: getLabel('checkout.payment.method.oney.select.title'),
            placeholder: getLabel('checkout.payment.method.oney.select.placeholder')
        })
    , [
        getLabel
    ]);
    return(/*#__PURE__*/ React.createElement(Oney$1, {
        labels: labels,
        onChange: onChange,
        options: options
    }));
};

function _extends$1() {
    _extends$1 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$1.apply(this, arguments);
}
const Oney = (props)=>/*#__PURE__*/ React.createElement(OneyContainer, _extends$1({}, props))
;

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
// This component will create any Adyen web component that can be created from it's "action" prop.
//
// NOTE:
// currently only tested with the "redirect" action
const ActionContainer = ({ action  })=>{
    const ref = React.useRef(null);
    const checkout = useCheckout();
    // Mounts the action component if we receive an action from adyen
    React.useEffect(()=>{
        if (!checkout || !action) return;
        const mountAction = function() {
            var _ref = _asyncToGenerator(function*() {
                const c = yield checkout;
                const actionComponent = c === null || c === void 0 ? void 0 : c.createFromAction(action);
                var _current;
                return actionComponent === null || actionComponent === void 0 ? void 0 : actionComponent.mount((_current = ref.current) !== null && _current !== void 0 ? _current : '');
            });
            return function mountAction() {
                return _ref.apply(this, arguments);
            };
        }();
        const promise = mountAction();
        return ()=>{
            promise.then((component)=>{
                return component === null || component === void 0 ? void 0 : component.unmount();
            });
        };
    }, [
        checkout,
        action
    ]);
    if (!action) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        ref: ref,
        className: "adyen-action-component"
    }));
};

var _ownerName, _iban;
const toPaymentMethodFormState = (state)=>({
        isValid: state.isValid,
        data: {
            'sepa.ownerName': (_ownerName = state.data.paymentMethod.ownerName) !== null && _ownerName !== void 0 ? _ownerName : '',
            'sepa.ibanNumber': (_iban = state.data.paymentMethod.iban) !== null && _iban !== void 0 ? _iban : ''
        }
    })
;
const SepaContainer = ({ onChange  })=>{
    const setSepaState = React.useCallback((state)=>onChange(toPaymentMethodFormState(state))
    , [
        onChange, 
    ]);
    const checkout = useCheckout(setSepaState);
    return(/*#__PURE__*/ React.createElement(PaymentMethodForm, {
        type: AdyenPaymentType.Sepa,
        checkout: checkout
    }));
};

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
const Sepa = (props)=>/*#__PURE__*/ React.createElement(SepaContainer, _extends({}, props))
;

export { ActionContainer, AdyenPaymentType, AdyenProvider, CreditCard, Ideal, Mbway, Oney, PaymentMethodForm, Sepa, useAdyen };
//# sourceMappingURL=index.js.map
